import PropTypes from "prop-types";
import React from "react";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import split from "lodash/split";
import { FormattedMessage, injectIntl } from "react-intl";
import { propTypes } from "redux-form";
import {
	ALERT_TYPE,
	BOOK_EXTERNAL_PAYMENT_STATUS,
	BOOK_STATUS,
	EXTERNAL_PAYMENT_STATUS,
	FRANCE_DOM_COUNTRIES,
	OFFER_CONTRACT_TYPES,
	PASSENGER_TYPE,
	PAYMENT_COUPON_TYPE,
	PAYMENT_METHODS,
	PAYMENT_OPTIONS,
	RESOLUTION,
	USABLE_CREDITS_STATUS,
} from "app/constants";
import { scrollToElement } from "app/utils/scroller";
import AlertMessage from "app/pages/.shared/AlertMessage.jsx";
import Button from "app/pages/.shared/form/Button";
import InsurancesFieldset from "@brand/Payment/InsurancesFieldset";
import {
	contactFields,
	dependantPaymentCardFields,
	insuranceField,
	passengerFields,
	paymentFields,
	termsField,
} from "app/pages/Booking/Payment/paymentSchema";
import RelativeLink from "app/pages/.shared/RelativeLink";
import {
	sendTagOnInsuranceSelect,
	sendTagOnPaymentInputBlur,
	sendTagOnPaymentViewContact,
	sendTagOnPaymentViewCTA,
	sendTagOnPaymentViewInformation,
	sendTagOnPaymentViewPayment,
	sendTagOnScrollInsurance,
} from "app/utils/analytics";
import ContactOurExperts from "app/pages/.shared/ContactOurExperts";
import PriceChangedWarningMessage from "app/pages/Booking/Payment/PriceChangedWarningMessage";
import "./payment.scss";
import PaymentPassengersFieldset from "app/pages/Booking/Payment/PaymentPassengersFieldset";
import PaymentData from "@brand/PaymentData/PaymentData";
import PopoverButton from "app/pages/.shared/Popover/PopoverButton";
import List from "app/pages/.shared/List";
import PaymentPriceSummaryContainer from "app/pages/.shared/PriceSummary/PaymentPriceSummaryContainer";
import { brandPropTypes, paymentTypesValues } from "app/utils/propTypes";
import { PushNotificationContainer } from "app/pages/.shared/PushNotification/PushNotificationContainer";
import uniq from "lodash/uniq";
import IconInfo from "app/pages/.shared/static/icons/IconInfo";
import LoadingPlaceholder from "app/pages/.shared/LoadingPlaceholder";
import Modal from "react-modal";
import PaymentContactContainer from "app/pages/Booking/Payment/PaymentContactContainer";
import PaymentTermsInputContainer from "app/pages/Booking/Payment/PaymentTermsInput/PaymentTermsInputContainer";
import { Waypoint } from "react-waypoint";
import PaymentCouponsContainer from "app/pages/Booking/Payment/PaymentCoupons/PaymentCouponsContainer";
import LoaderBar from "app/pages/.shared/LoaderBar/LoaderBar";
import LineClampViewMore from "app/pages/.shared/LineClampViewMore/LineClampViewMore";
import IconLock from "app/pages/.shared/IconLock";
import HolidaySummaryContainer from "app/pages/Booking/HolidaySummary/HolidaySummary/HolidaySummaryContainer";

const MODAL_STYLE = {
	overlay: {
		backgroundColor: "rgba(43, 56, 63, .65)",
		zIndex: 6,
	},
	content: {
		top: 0,
		right: 0,
		left: 0,
		bottom: 0,
		border: "none",
		background: "none",
		overflow: "hidden",
		padding: "50px",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
};

class Payment extends React.Component {
	constructor() {
		super();
		this.contactRef = React.createRef();
		this.paymentErrorMessageRef = React.createRef();
		this.paymentMethodsErrorMessageRef = React.createRef();
		this.onInsuranceSelection = this.onInsuranceSelection.bind(this);
		this.onCountryChange = this.onCountryChange.bind(this);
		this.scrollToErrorMessage = this.scrollToErrorMessage.bind(this);
		this.handleInputBlur = this.handleInputBlur.bind(this);
		this.handleCreditCardInformationChange = this.handleCreditCardInformationChange.bind(this);
		this.handleKeyPressSubmit = this.handleKeyPressSubmit.bind(this);
		this.selectCouponTypePromo = this.selectCouponTypePromo.bind(this);
	}

	handleKeyPressSubmit(event) {
		if (event.key === "Enter") {
			event.preventDefault(); //permet d'arrêter le submit
		}
	}

	handleInputBlur(event) {
		let value = event.target.value;

		if (event.target.type === "checkbox") {
			value = event.target.checked;
		}

		if (value || value === false) {
			sendTagOnPaymentInputBlur(event.target.name);
		}
	}

	onCountryChange(event) {
		// Si le user a selectionné casino alors que celui-ci n'est pas autorisé, alors on sélectionne le paiement par CB par défaut
		if (
			FRANCE_DOM_COUNTRIES.indexOf(event.target.value) === -1 &&
			this.props.fields.payment.type.value === PAYMENT_METHODS.CASINO
		) {
			this.props.fields.payment.type.onChange(PAYMENT_METHODS.CARD);
			this.props.fields.payment.mode.onChange(String(PAYMENT_OPTIONS["1x"]));

			this.props.updateBooking({
				paymentType: PAYMENT_METHODS.CARD,
				paymentMode: PAYMENT_OPTIONS["1x"],
			});
		}
	}

	selectCouponTypePromo() {
		this.props.updateBooking({
			coupon: PAYMENT_COUPON_TYPE.PROMOCODE,
		});
	}

	handleCreditCardInformationChange(event) {
		const { fieldName: touchedFieldName } = event.target.dataset;
		const { booking, fields } = this.props;
		const cardPathName = booking.paymentType === PAYMENT_METHODS.BCMC ? "cardBCMC" : "card";

		fields.payment[cardPathName][touchedFieldName].onChange(event);
		this.triggerChangeOnOtherDependantPaymentCardFields(touchedFieldName);
	}

	triggerChangeOnOtherDependantPaymentCardFields(touchedFieldName) {
		const { booking, fields } = this.props;
		const cardPathName = booking.paymentType === PAYMENT_METHODS.BCMC ? "cardBCMC" : "card";

		dependantPaymentCardFields.forEach(fieldName => {
			if (fieldName !== touchedFieldName && fields.payment[cardPathName][fieldName].value) {
				fields.payment[cardPathName][fieldName].onChange(
					fields.payment[cardPathName][fieldName].value
				);
			}
		});
	}

	onInsuranceSelection(insurance) {
		const { total, payment, fields, updateBooking } = this.props;
		const totalPlusInsurance = total + insurance.price;
		const data = { insurance };

		if (
			(totalPlusInsurance < payment.payment4x.minAmount ||
				totalPlusInsurance > payment.payment4x.maxAmount) &&
			fields.payment.type.value === PAYMENT_METHODS.CASINO &&
			fields.payment.mode.value === String(PAYMENT_OPTIONS["4x"])
		) {
			data.paymentType = PAYMENT_METHODS.CARD;
			data.paymentMode = PAYMENT_OPTIONS["1x"];
			fields.payment.type.onChange(PAYMENT_METHODS.CARD);
			fields.payment.mode.onChange(String(PAYMENT_OPTIONS["1x"]));
		}

		if (
			totalPlusInsurance > payment.paymentSwissbilling.maxAmount &&
			fields.payment.type.value === PAYMENT_METHODS.SWISS_BILLING
		) {
			data.paymentType = PAYMENT_METHODS.CARD;
			fields.payment.type.onChange(PAYMENT_METHODS.CARD);
		}

		// permet entre autre de mettre à jour le montant total de la réservation à la sélection de l'assurance
		updateBooking(data);

		sendTagOnInsuranceSelect(insurance);
	}

	scrollToErrorMessage(errors) {
		let errorMessageRef;
		const contactErrorList = [];
		const paymentErrorList = [];
		const insuranceErrorList = [];
		const termsErrorList = [];
		const passengersErrorList = [];

		contactFields.forEach(path => {
			const error = get(errors, path);
			if (error) {
				contactErrorList.push(error);
			}
		});

		if (contactErrorList.length > 0) {
			errorMessageRef = this.contactRef.current;
		} else {
			passengerFields.forEach(path => {
				this.props.fields.passengers.forEach((passenger, index) => {
					const parts = split(path, "[]");
					const error = get(errors, `${parts[0]}[${index}]${parts[1]}`);
					if (error) {
						passengersErrorList.push(error);
					}
				});
			});

			if (passengersErrorList.length > 0) {
				errorMessageRef = this.refs.passengersErrorMessage;
			} else {
				insuranceField.forEach(path => {
					const error = get(errors, path);
					if (error) {
						insuranceErrorList.push(error);
					}
				});

				if (insuranceErrorList.length > 0) {
					errorMessageRef = this.refs.insuranceErrorMessage;
				} else {
					paymentFields.forEach(path => {
						const error = get(errors, path);
						if (error) {
							paymentErrorList.push(error);
						}
					});

					if (paymentErrorList.length > 0) {
						errorMessageRef =
							this.paymentMethodsErrorMessageRef &&
							this.paymentMethodsErrorMessageRef.current;
					} else {
						termsField.forEach(path => {
							const error = get(errors, path);
							if (error) {
								termsErrorList.push(error);
							}
						});

						if (termsErrorList.length > 0) {
							errorMessageRef = this.refs.termsErrorMessage;
						}
					}
				}
			}
		}

		if (errorMessageRef) {
			scrollToElement(errorMessageRef, -100);
		}
	}

	componentWillUpdate(nextProps) {
		const errors = nextProps.errors;
		const bookStatus = nextProps.payment.bookStatus;

		if (
			bookStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ABORTED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_3DS_REFUSED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_REFUSED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_INFO ||
			bookStatus === BOOK_STATUS.PAYMENT_AGREEMENT_REFUSED ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_TECHNICAL_ERROR ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_UNKNOWN ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_REFUSED ||
			bookStatus === BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR
		) {
			if (this.paymentErrorMessageRef && this.paymentErrorMessageRef.current) {
				scrollToElement(this.paymentErrorMessageRef.current, -100);
			}
		} else if (isEmpty(this.props.errors) && errors) {
			// on scroll vers le premier éventuel bloc de message d'erreur
			// TODO a optimiser ou refactorer
			if (errors && !isEmpty(errors)) {
				this.scrollToErrorMessage(errors);
			} else if (nextProps.error) {
				scrollToElement(this.refs.errorMessage, -100);
			}
		}
	}

	render() {
		const {
			fields = {},
			handleSubmit,
			submitting,
			booking = {},
			payment = {},
			error, // set to undefined on each redux-form/CHANGE
			errors,
			location = {},
			resolution,
			productUri,
			dateOfBirthRequired,
			contractRemarks,
			isChildrenAgeRequired,
			showFrequentTravellerCardInput,
			intl,
			selectedPaymentType,
			shop = "",
			showInsurancesSection,
			onFetchProfilSuccess,
			onFetchProfilFail,
			assuranceMultirisquesSansMotifDocumentName,
			assuranceAnnulationSansMotifDocumentName,
			cartAmount,
			usableCouponsStatus,
			isSpecialOfferActive,
			isOperatorMode,
			brandDisplayName,
		} = this.props;

		const {
			contact: { name, address, email, phone },
			passengers,
			insurance,
			terms,
		} = fields;

		const paymentErrorList = [];
		const insuranceErrorList = [];
		const termsErrorList = [];
		const passengersErrorList = [];

		paymentFields.forEach(path => {
			const error = get(errors, path);

			if (
				error &&
				((selectedPaymentType &&
					((path.match(/\.card\./) && selectedPaymentType === PAYMENT_METHODS.CARD) ||
						(path.match(/issuerId/) && selectedPaymentType === PAYMENT_METHODS.IDEAL) ||
						(path.match(/\.cardBCMC\./) &&
							selectedPaymentType === PAYMENT_METHODS.BCMC) ||
						(path.match(/\.swissBilling\./) &&
							selectedPaymentType === PAYMENT_METHODS.SWISS_BILLING))) ||
					path.match(/\.card\./))
			) {
				paymentErrorList.push(intl.formatMessage(error));
			}
		});

		insuranceField.forEach(path => {
			const error = get(errors, path);
			if (error) {
				insuranceErrorList.push(intl.formatMessage(error));
			}
		});

		termsField.forEach(path => {
			const error = get(errors, path);
			if (error) {
				termsErrorList.push(intl.formatMessage(error));
			}
		});

		passengerFields.forEach(path => {
			fields.passengers.forEach((passenger, index) => {
				const parts = split(path, "[]");
				const error = get(errors, `${parts[0]}[${index}]${parts[1]}`);
				if (error) {
					const type = passenger.type;
					let errorPassengerID;

					if (type.value === PASSENGER_TYPE.CHILD) {
						errorPassengerID = "payment.passengers.child";
					} else if (type.value === PASSENGER_TYPE.INFANT) {
						errorPassengerID = "payment.passengers.infant";
					} else {
						errorPassengerID = "payment.passengers.adult";
					}
					passengersErrorList.push(
						`${intl.formatMessage(
							{ id: errorPassengerID },
							{ index: index + 1 }
						)}: ${intl.formatMessage(error)}`
					);
				}
			});
		});

		const bookStatus = payment.bookStatus;

		const isPaymentError =
			bookStatus === BOOK_STATUS.CREDIT_CARD_TECHNICAL_ERROR ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_3DS_REFUSED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_REFUSED ||
			bookStatus === BOOK_STATUS.CREDIT_CARD_INFO ||
			bookStatus === BOOK_STATUS.PAYMENT_AGREEMENT_REFUSED;

		const isExternalPaymentError =
			bookStatus === BOOK_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR ||
			bookStatus === EXTERNAL_PAYMENT_STATUS.EXTERNAL_PAYMENT_STATUS_ERROR ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_TECHNICAL_ERROR ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_REFUSED ||
			bookStatus === BOOK_EXTERNAL_PAYMENT_STATUS.BOOK_EXTERNAL_PAYMENT_STATUS_UNKNOWN;

		const isPassengersError =
			bookStatus === BOOK_STATUS.CHILDREN_AGES_DISCREPANCY ||
			bookStatus === BOOK_STATUS.INFANTS_AGES_DISCREPANCY;

		const isNothingToPay = cartAmount === 0;
		const isCouponsLoading =
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_CREDIT_NOTES ||
			usableCouponsStatus === USABLE_CREDITS_STATUS.LOADING_COUPONS;

		let paymentLabelConfirmButton = "payment.submit";

		if (isNothingToPay) {
			paymentLabelConfirmButton = "payment.submit.nothing.to.pay";
		} else if (
			booking.paymentType === PAYMENT_METHODS.IDEAL ||
			booking.paymentType === PAYMENT_METHODS.SOFORT ||
			booking.paymentType === PAYMENT_METHODS.GIROPAY ||
			booking.paymentType === PAYMENT_METHODS.POSTFINANCE_EFINANCE ||
			booking.paymentType === PAYMENT_METHODS.POSTFINANCE_CARD ||
			booking.paymentType === PAYMENT_METHODS.PAYPAL ||
			booking.paymentType === PAYMENT_METHODS.PLEDG
		) {
			paymentLabelConfirmButton = "payment.external.submit.continue";
		} else if (booking.paymentType === PAYMENT_METHODS.SWISS_BILLING) {
			paymentLabelConfirmButton = "payment.submit.only.validate";
		}

		const isMobile = resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM;

		return (
			<React.Fragment>
				<form
					className="payment"
					ref="errorMessage"
					onSubmit={handleSubmit}
					onKeyPress={this.handleKeyPressSubmit}
				>
					{isMobile && (
						<div className="payment__my-booking">
							<HolidaySummaryContainer showProductPicture />
						</div>
					)}

					{error &&
						isEmpty(errors) &&
						!isPaymentError &&
						!isExternalPaymentError &&
						!isPassengersError && (
							<AlertMessage
								message={
									<FormattedMessage
										values={{
											link: (
												<RelativeLink
													to={{ pathname: "/booking/quote" }}
													design="naked"
												>
													<FormattedMessage id="error.no.avaibilities.link.label" />
												</RelativeLink>
											),
										}}
										{...error}
									/>
								}
							/>
						)}

					{contractRemarks && (
						<AlertMessage
							message={
								<LineClampViewMore
									enableShowMore={true}
									lineCount={4}
									text={contractRemarks}
								/>
							}
						/>
					)}

					{location.state && (
						<PriceChangedWarningMessage
							show={location.state.priceChanged}
							offerType={get(booking, "offer.type")}
							newPrice={location.state.newPrice}
							oldPrice={location.state.oldPrice}
						/>
					)}

					{bookStatus === BOOK_STATUS.WARNING_FULL_PROPERTY ? (
						<ContactOurExperts
							message={<FormattedMessage id="confirmation.contact.headline.short" />}
						/>
					) : (
						<React.Fragment>
							<React.Fragment>
								<div
									className="payment__item"
									style={{ order: isOperatorMode ? "1" : "0" }}
								>
									<header className="payment__title">
										<FormattedMessage id="payment.contact.title" />
									</header>

									<Waypoint onEnter={sendTagOnPaymentViewContact}>
										<div className="payment__content" ref={this.contactRef}>
											<PaymentContactContainer
												initializeForm={this.props.initializeForm}
												formInitialValues={this.props.formInitialValues}
												addressField={address}
												errors={errors}
												emailField={email}
												handleInputBlur={this.handleInputBlur}
												nameField={name}
												onCountryChange={this.onCountryChange}
												phoneField={phone}
												onFetchProfilSuccess={onFetchProfilSuccess}
												onFetchProfilFail={onFetchProfilFail}
											/>
										</div>
									</Waypoint>
								</div>

								{get(booking, "offer.contractType") !==
									OFFER_CONTRACT_TYPES.RENTAL && (
									<div
										className="payment__item"
										ref="passengersErrorMessage"
										style={{ order: "2" }}
									>
										<Waypoint onEnter={sendTagOnPaymentViewInformation}>
											<header className="payment__title">
												<FormattedMessage id="payment.passengers.title" />
											</header>
										</Waypoint>
										<div
											className="payment__content"
											ref="passengersErrorMessage"
										>
											{uniq(passengersErrorList).length > 0 && (
												<AlertMessage
													alertType={ALERT_TYPE.ERROR}
													message={
														<FormattedMessage id="error.booking.payment.fields.required" />
													}
													errors={uniq(passengersErrorList)}
												/>
											)}

											<div className="payment__disclaimer">
												<FormattedMessage id="payment.disclamer" />
											</div>
											<div className="payment__passengers">
												<PaymentPassengersFieldset
													bookStatus={bookStatus}
													resolution={resolution}
													productUri={productUri}
													dateOfBirthRequired={dateOfBirthRequired}
													showFrequentTravellerCardInput={
														showFrequentTravellerCardInput
													}
													isChildrenAgeRequired={isChildrenAgeRequired}
													onBlur={this.handleInputBlur}
													passengers={passengers}
													error={error}
													showError={error && isPassengersError}
												/>
											</div>
											{!showInsurancesSection && (
												<div className="payment__insurance-note">
													<FormattedMessage id="payment.passengers.insurance.note" />
												</div>
											)}
										</div>
									</div>
								)}

								{showInsurancesSection && (
									<div
										className="payment__item"
										style={{ order: isOperatorMode ? "0" : "3" }}
									>
										<header className="payment__title" ref="insurance">
											<FormattedMessage id="payment.insurance.title" />
										</header>
										<div
											className="payment__content"
											ref="insuranceErrorMessage"
										>
											{insuranceErrorList.length > 0 && (
												<AlertMessage
													alertType={ALERT_TYPE.ERROR}
													message={
														<FormattedMessage id="error.payment.insurance.required" />
													}
												/>
											)}

											<Waypoint onEnter={sendTagOnScrollInsurance} />
											<InsurancesFieldset
												intro={payment.insurances.intro}
												logo={payment.insurances.logo}
												onChange={this.onInsuranceSelection}
												insurances={payment.insurances.insurancesItem}
												field={insurance}
												assuranceMultirisquesSansMotifDocumentName={
													assuranceMultirisquesSansMotifDocumentName
												}
												assuranceAnnulationSansMotifDocumentName={
													assuranceAnnulationSansMotifDocumentName
												}
												resolution={resolution}
												shop={shop}
												isSpecialOfferActive={isSpecialOfferActive}
											/>
										</div>
									</div>
								)}

								<div className="payment__item" style={{ order: "4" }}>
									<PaymentCouponsContainer field={fields.coupon} />
								</div>

								{(!isNothingToPay || isCouponsLoading) && (
									<div className="payment__item" style={{ order: "5" }}>
										<header className="payment__title">
											<FormattedMessage id="payment.payment.title" />
										</header>
										<div className="payment__security-note">
											<IconLock />
											<FormattedMessage id="payment.security.full" />

											<PopoverButton
												popOverClassName="payment__popover"
												popOverButtonClassName="payment__popover__button"
												popOverPosition="below"
												popOverContent={
													<div className="payment__popover--content">
														<List
															items={[
																<FormattedMessage
																	id="payment.security.note.1"
																	key="payment.security.note.1"
																/>,
																<FormattedMessage
																	id="payment.security.note.2"
																	key="payment.security.note.2"
																/>,
																<FormattedMessage
																	id="payment.security.note.3"
																	key="payment.security.note.3"
																	values={{
																		brandName: brandDisplayName,
																	}}
																/>,
															]}
														/>
													</div>
												}
												buttonLabel={
													<IconInfo
														width={15}
														height={15}
														style={{ marginLeft: "5px" }}
													/>
												}
											/>
										</div>
										{isCouponsLoading ? (
											<div className="payment__content">
												<LoaderBar />
											</div>
										) : (
											<Waypoint onEnter={sendTagOnPaymentViewPayment}>
												<div className="payment__content">
													<PaymentData
														error={error}
														fields={fields}
														isExternalPaymentError={
															isExternalPaymentError
														}
														isPaymentError={isPaymentError}
														paymentErrorList={uniq(paymentErrorList)}
														onCreditCardInputsBlur={
															this.handleInputBlur
														}
														onCreditCardInformationChange={
															this.handleCreditCardInformationChange
														}
														selectedPaymentType={selectedPaymentType}
														shop={shop}
														paymentModesRef={
															this.paymentErrorMessageRef
														}
														paymentMethodsRef={
															this.paymentMethodsErrorMessageRef
														}
													/>
												</div>
											</Waypoint>
										)}
									</div>
								)}
							</React.Fragment>
							<Waypoint onEnter={sendTagOnPaymentViewCTA}>
								<div className="payment__pay-button" style={{ order: "6" }}>
									<PaymentPriceSummaryContainer />

									<div ref="termsErrorMessage">
										{termsErrorList.length > 0 && (
											<AlertMessage
												alertType={ALERT_TYPE.ERROR}
												message={
													<FormattedMessage id="error.payment.must.accept.terms" />
												}
											/>
										)}

										<div className="payment__terms">
											<PaymentTermsInputContainer
												onBlur={this.handleInputBlur}
												field={terms}
												brandDisplayName={brandDisplayName}
											/>
										</div>
									</div>

									<Button
										design="primary"
										submit
										onClick={() => {
											this.scrollToErrorMessage(this.props.errors);
										}}
										loading={submitting}
										disabled={submitting}
										data-cy="payment-button"
									>
										<FormattedMessage id={paymentLabelConfirmButton} />
									</Button>
									<div className="payment__mandatory-label">
										<FormattedMessage id="payment.mandatory.fields.label" />
									</div>
								</div>
							</Waypoint>
						</React.Fragment>
					)}
					<PushNotificationContainer />
				</form>
				<Modal isOpen={bookStatus === BOOK_STATUS.LOADING} style={MODAL_STYLE}>
					<div className="payment-modal__loading-placeholder">
						<LoadingPlaceholder
							message={<FormattedMessage id="payment.external.wait.message" />}
						/>
					</div>
				</Modal>
			</React.Fragment>
		);
	}
}

Payment.propTypes = {
	...propTypes,
	profile: PropTypes.object,
	cartAmount: PropTypes.number,
	intl: PropTypes.object,
	usableCouponsStatus: PropTypes.string,
	partnerCode: PropTypes.string,
	vpSaleCode: PropTypes.string,
	contractRemarks: PropTypes.string,
	shop: PropTypes.string,
	updateBooking: PropTypes.func,
	onFetchProfilSuccess: PropTypes.func,
	onFetchProfilFail: PropTypes.func,
	showFrequentTravellerCardInput: PropTypes.bool,
	selectedPaymentType: paymentTypesValues,
	showInsurancesSection: PropTypes.bool,
	assuranceMultirisquesSansMotifDocumentName: PropTypes.string,
	assuranceAnnulationSansMotifDocumentName: PropTypes.string,
	brand: brandPropTypes,
	isSpecialOfferActive: PropTypes.bool,
	isOperatorMode: PropTypes.bool,
	brandDisplayName: PropTypes.string,
};

export default injectIntl(Payment);
